import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import type { Bundle, ProductsListItem } from '@techgitter/contracts';
import { fetchInventory } from '../services/inventoryApi';
import {
  fetchBundles,
  setBundleFlag,
  addBundleComponent,
  removeBundleComponent,
} from '../services/bundlesApi';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../components/table';
import { Chip } from '../components/chip';
import { CARD_SX } from '../styles/designSystem';

// ---------------------------------------------------------------------------
// Server-search commercial-product picker. Any product (not prefix-limited),
// so a future Checkpoint/Dropsuite SKU is reachable as soon as it exists in RS.
// ---------------------------------------------------------------------------

interface ProductPickerProps {
  label: string;
  value: ProductsListItem | null;
  onChange: (p: ProductsListItem | null) => void;
  excludeIds?: string[];
  helperText?: string;
}

const ProductPicker: React.FC<ProductPickerProps> = ({
  label,
  value,
  onChange,
  excludeIds = [],
  helperText,
}) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ProductsListItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Debounced server search — the catalog is large, so we never load it all;
  // type ≥2 chars and the API filters on name + SKU.
  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      fetchInventory({
        page: 1,
        pageSize: 50,
        search: q,
        sort: 'displayName',
        order: 'asc',
        signal: controller.signal,
      })
        .then((res) => setOptions(res.items.filter((p) => !p.disabled)))
        .catch((e) => {
          if (!(e instanceof Error && e.name === 'AbortError')) setOptions([]);
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [input]);

  const visible = useMemo(
    () => options.filter((o) => !excludeIds.includes(o.id)),
    [options, excludeIds],
  );

  return (
    <Autocomplete
      sx={{ minWidth: 360 }}
      size="small"
      options={visible}
      loading={loading}
      value={value}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInput(v)}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.displayName || o.sku || ''}
      getOptionKey={(o) => o.id}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      noOptionsText={input.trim().length < 2 ? 'Type to search products…' : 'No matching products'}
      renderOption={(props, o) => {
        const { key, ...liProps } = props as { key?: string };
        return (
          <li {...liProps} key={o.id}>
            <Stack>
              <span style={{ fontSize: 13 }}>{o.displayName || o.sku}</span>
              {o.sku && o.sku !== o.displayName && (
                <Typography variant="caption" color="text.secondary">
                  {o.sku}
                </Typography>
              )}
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} size="small" helperText={helperText} />
      )}
    />
  );
};

// ---------------------------------------------------------------------------
// Bundles admin page
// ---------------------------------------------------------------------------

/**
 * Admin surface for **bundles**: mark a commercial product as a bundle
 * (the SM4RT-only is_bundle flag, never synced to RepairShopr) and manage its
 * component licenses (the bill-of-materials). Reconciliation coverage expands a
 * billed bundle line through these components (1 per bundle seat), then each
 * component license through its own commercial↔vendor mapping. Components whose
 * commercial product / vendor mapping don't exist yet (Checkpoint/Dropsuite)
 * simply contribute nothing until they're added. See
 * docs/architecture/subscriptions-reconciliation-status.md §3v.
 */
export const BundlesAdminPage: React.FC = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // "Add a bundle" picker.
  const [newBundle, setNewBundle] = useState<ProductsListItem | null>(null);
  // Per-bundle "add component" picker selection, keyed by bundle product id.
  const [componentPick, setComponentPick] = useState<Record<string, ProductsListItem | null>>({});

  const notify = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const items = await fetchBundles(signal);
      if (!signal?.aborted) setBundles(items);
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Failed to load bundles');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const markAsBundle = async () => {
    if (!newBundle) return;
    setBusy(true);
    try {
      await setBundleFlag(newBundle.id, true);
      notify(`"${newBundle.displayName || newBundle.sku}" is now a bundle.`);
      setNewBundle(null);
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to flag bundle', 'error');
    } finally {
      setBusy(false);
    }
  };

  const unflagBundle = async (bundle: Bundle) => {
    setBusy(true);
    try {
      await setBundleFlag(bundle.productId, false);
      notify(`"${bundle.name || bundle.sku}" is no longer a bundle.`);
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to remove bundle', 'error');
    } finally {
      setBusy(false);
    }
  };

  const addComponent = async (bundle: Bundle) => {
    const pick = componentPick[bundle.productId];
    if (!pick) return;
    setBusy(true);
    try {
      await addBundleComponent(bundle.productId, pick.id);
      notify(`Added "${pick.displayName || pick.sku}".`);
      setComponentPick((cur) => ({ ...cur, [bundle.productId]: null }));
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to add component', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeComponent = async (componentId: string) => {
    setBusy(true);
    try {
      await removeBundleComponent(componentId);
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Failed to remove component', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box sx={{ p: '12px 16px 24px' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A bundle is a commercial product sold as one line but provisioned as several
          component licenses (1 per bundle seat). Mark the product as a bundle and assign
          its component licenses below — reconciliation coverage expands a billed bundle
          through these components. Components not yet created/mapped (e.g. Checkpoint,
          Dropsuite) contribute nothing until added.
        </Typography>

        {/* Add a bundle */}
        <Box sx={[CARD_SX, { p: 2, mb: 1 }]}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <ProductPicker
              label="Mark a commercial product as a bundle"
              value={newBundle}
              onChange={setNewBundle}
              excludeIds={bundles.map((b) => b.productId)}
              helperText="Search by name or SKU (e.g. BML.SP)"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!newBundle || busy}
              onClick={markAsBundle}
            >
              Mark as bundle
            </Button>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : bundles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No bundles yet. Mark a commercial product as a bundle above.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {bundles.map((bundle) => {
              const exclude = [
                bundle.productId,
                ...bundle.components.map((c) => c.componentProductId),
              ].filter((x): x is string => !!x);
              return (
                <Box key={bundle.productId} sx={[CARD_SX, { p: 2 }]}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {bundle.name || bundle.sku}
                      </Typography>
                      {bundle.sku && bundle.sku !== bundle.name && (
                        <Typography variant="caption" color="text.secondary">
                          {bundle.sku}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Remove the bundle flag (does not delete the product)">
                      <span>
                        <Button
                          size="small"
                          color="inherit"
                          disabled={busy}
                          onClick={() => unflagBundle(bundle)}
                        >
                          Unflag bundle
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    Component licenses{' '}
                    <Box component="span" sx={{ ml: 0.5 }}>
                      <Chip>{bundle.components.length}</Chip>
                    </Box>
                  </Typography>

                  {bundle.components.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                      No component licenses assigned yet.
                    </Typography>
                  ) : (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <DataTableCard>
                        <DataTable>
                          <thead>
                            <tr>
                              <DataTableHeadCell>License</DataTableHeadCell>
                              <DataTableHeadCell>SKU</DataTableHeadCell>
                              <DataTableHeadCell align="right">Remove</DataTableHeadCell>
                            </tr>
                          </thead>
                          <tbody>
                            {bundle.components.map((c) => (
                              <DataTableRow key={c.id}>
                                <DataTableCell>
                                  {c.componentName || c.componentSku || '—'}
                                </DataTableCell>
                                <DataTableCell>{c.componentSku || '—'}</DataTableCell>
                                <DataTableCell align="right">
                                  <Tooltip title="Remove component">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={busy}
                                        onClick={() => removeComponent(c.id)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </DataTableCell>
                              </DataTableRow>
                            ))}
                          </tbody>
                        </DataTable>
                      </DataTableCard>
                    </Box>
                  )}

                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                    <ProductPicker
                      label="Add a component license"
                      value={componentPick[bundle.productId] ?? null}
                      onChange={(p) =>
                        setComponentPick((cur) => ({ ...cur, [bundle.productId]: p }))
                      }
                      excludeIds={exclude}
                      helperText="Any commercial product (M365, Checkpoint, Dropsuite, …)"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      disabled={!componentPick[bundle.productId] || busy}
                      onClick={() => addComponent(bundle)}
                    >
                      Add component
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BundlesAdminPage;
